//table pagination page size0
export interface PaginatedApiParams {
  page?: number,
  page_size?: number,
}

export interface GetAccountingStatementsListParams {
  ordering?: string;
  reconciliation_group?: string;
}

export enum JOB_STATUS {
  PENDING = "P",
  DONE = "D",
  ERROR = "E",
}

export interface AccountingStatement {
  uuid: string,
  document: string,
  document_name: string,
  broker: string,
  rore_job_status: JOB_STATUS | null,
  reconciliation_job_status: JOB_STATUS | null,
  ec_period: string,
  ec_number: string,
  total_credit: string | null, //numero con due decimali
  credit_sum_validation: boolean,
  total_debit: string | null,
  debit_sum_validation: boolean,
  diff_total_credit_debit: string | null,
  ec_period_validation: string,
  sums_validations: string
  balance: number | null,
  balance_validation: boolean,
  withholding_tax: number | null,
  has_titles: boolean,
  date_creation: string, //"2025-04-23T16:47:46.023806Z"
  is_deletable: boolean,
  to_be_reconciled: boolean,
  reconciliation_group: string,
}

export interface StatementTitleParams {
  broker?: string,
  confirmed?: string,
  contractor?: string,
  credit_max?: string,
  credit_min?: string,
  credit_status?: string,
  debit_max?: string,
  debit_min?: string,
  debit_status?: string,
  credit_debit_status?: string,
  ec_number?: string,
  ec_period_max?: string,
  ec_period_min?: string,
  lob?: string,
  policy_effective_date_max?: string,
  policy_effective_date_min?: string,
  policy_expiring_date_max?: string,
  policy_expiring_date_min?: string,
  policy_number?: string,
  matched?: boolean,
  title_effective_date_max?: string,
  title_effective_date_min?: string,
  title_receipt_date_max?: string,
  title_receipt_date_min?: string,
  ordering?: string,
}

export interface GetStatementTitleListParams extends StatementTitleParams {
  statement_uuid?: string,
  remittance_uuid?: string,
  reconciliation_group?: string,
  to_be_reconciled?: boolean,
}

export interface GetEmittedTitlesListParams {
  broker?: string;
  broker_name?: string;
  contract_code?: string;
  contractor?: string;
  credit_max?: string;
  credit_min?: string;
  debit_max?: string;
  debit_min?: string;
  issuance_type?: string;
  movement_code?: string;
  negotiation?: string;
  ordering?: string;
  paginate?: boolean;
  product_code?: string;
  reconciled?: boolean;
  start_date_after?: string;
  start_date_before?: string;
  taxable_amount_max?: string;
  taxable_amount_min?: string;
  tracking_lost?: boolean;
}

export interface GetLinkedEmittedTitleParams extends GetEmittedTitlesListParams {
  uuid: string;
}


export type StatementTitleFields = {
  lob: string | null,
  contractor: string | null,
  debit: number,
  credit: number,
  title_number: string | null,
  policy_number: string | null,
  title_receipt_date: string | null, //"2024-08-05",
  policy_expiring_date: string | null, //"2025-01-31",
  title_effective_date: string | null, //"2024-01-31",
  policy_effective_date: string | null,
  ec_number: string | null,
  ec_period: string | null,
}

export type StatementTitleValidationFields = {
  [key in keyof StatementTitleFields as `${key}_validation`]: boolean;
}

export type StatementTitleDeltaFields = {
  [key in keyof Pick<StatementTitleFields, "credit" | "debit"> as `${key}_delta`]: null | number;
}

export enum CreditDebitStatus {
  NP = "NP",
  OK = "OK",
  KO = "KO",
  PENDING = "PENDING",
  WARNING = "WARNING",
}

export enum RemittanceType {
  archived = "A",
  standard = "R"
}

export type StatementTitleStatusFields = {
  [key in keyof Pick<StatementTitleFields, "credit" | "debit"> as `${key}_status` | "credit_debit_status"]: CreditDebitStatus;
}

export type StatementTitle = {
  uuid: string,
  accounting_statement: string,
  broker: string | null,
  is_one_broken: boolean,
  raw_data: StatementTitleFields,
  matched: boolean,
  confirmed: boolean,
  inbound_remittance: string | null,
  is_single?: boolean,
} & StatementTitleFields & StatementTitleValidationFields & StatementTitleDeltaFields & StatementTitleStatusFields;

export type EmittedTitle = {
  uuid: string;
  broker: string;
  broker_name: string;
  backlog: string;
  backlogs: string[];
  tracking_lost: boolean;
  document_name: string;
  negotiation: string | null;
  movement_code: string;
  collective_contract_code: string;
  contract_code: string;
  product_code: string;
  contractor: string;
  start_date: string;
  credit: string;
  debit: string;
  taxable_amount: string;
  issuance_type: string;
}

export type LinkedEmittedTitle = EmittedTitle

export interface GetLinkedEmittedTitlesListResponse {
  count: number,
  next: string | null,
  previous: string | null,
  results: {data:  LinkedEmittedTitle[], credit_sum: number, debit_sum: number }
}

export type EmittedTitleDocument = {
  name: string,
  date_creation: string,
}

export interface CreateStatementDocumentParams {
  path: string,
  name: string,
  content_type: string,
  broker: string,
  to_be_reconciled: boolean,
}

export interface CreateEmittedTitleDocumentParams {
  path: string,
  name: string,
  content_type: string,
}

export interface DocumentS3UploadParams {
  url: string,
  fields: {
    "acl": "string",
    "Content-Type": "string",
    "key": "string",
    "x-amz-algorithm": "string",
    "x-amz-credential": "string",
    "x-amz-date": "string",
    "policy": "string",
    "x-amz-signature": "string"
  }
}

export type CreateStatementDocumentResponse = {
  uuid: string,
  statement: string,
} & DocumentS3UploadParams

export type CreateEmittedTitleDocumentResponse = {
  uuid: string,
} & DocumentS3UploadParams

export interface Document {
  uuid: string,
  is_valid: boolean,
  path: string,
  name: string,
  date_creation: string, //"2025-04-09T16:45:12.388Z",
  date_edit: string, //"2025-04-09T16:45:12.388Z",
  user_creation: string,
  user_edit: string,
}

export interface PatchDocumentParams {
  uuid: string,
  is_valid?: boolean,
  path?: string,
  name?: string
}

export type StatementTitleDownloadParams = StatementTitleParams
export type DownloadAccountingStatementTitleParams = { uuid: string } & StatementTitleParams

export interface InboundRemittance {
  uuid: string,
  date_creation: string,
  user_creation: string,
  name: string,
  notes: string,
  remittance_type: RemittanceType,
}

export interface GetInboundRemittanceApiParams {
  search?: string;
  remittance_type?: RemittanceType;
}

export interface ReconciliationGroup {
  uuid: string,
  broker: string,
  broker_name: string,
  unreconciled_credit: string | null,
  unreconciled_debit: string | null,
  ec_period: string | null,
  reconciliation_job_status: JOB_STATUS | null,
  rore_job_status: JOB_STATUS | null,
}

export interface GetReconciliationGroupsApiParams {
  broker?: string;
  broker_name?: string;
  ordering?: string;
  uuid?: string;
  ec_period?: string ;
}

export interface StatementTitleRelatedNegotiation {
    "uuid":  string,
    "broker": string,
    "contractor": string,
    "policy_number": string,
    "state": string,
    "lob": string,
    "fee_broker_perc": number,
    "policy_effective_date": string,
    "policy_expiring_date": string,
    "quoted_premium": number,
    "gross_premium": number,
    "fractional_gross_premium": number,
    "delta_neg": number,
  }